import { useQuery, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useWorkspaceKeys, type WorkspaceKeys } from "../../app/current-user.tsx";
import { researchApi } from "../../client.ts";
import type { SampleInput, SampleSummary, UpdateSampleInput } from "../../domain/schema.ts";
import type { WorkspaceWrite } from "../../lib/workspace-cache.ts";
import { useWorkspaceMutation } from "../../lib/workspace-mutation.ts";
import { workspaceQuery, type WorkspaceQuery } from "../../lib/workspace-query.ts";

export const samplesQuery = (keys: WorkspaceKeys): WorkspaceQuery<SampleSummary[]> =>
  workspaceQuery(keys.samples(), () => researchApi.listSamples());

export const sampleQuery = (keys: WorkspaceKeys, id: string): WorkspaceQuery<SampleSummary> =>
  workspaceQuery(keys.sample(id), () => researchApi.getSample(id));

export const useSamples = (): UseQueryResult<SampleSummary[]> =>
  useQuery(samplesQuery(useWorkspaceKeys()));

export const useSample = (id: string): UseQueryResult<SampleSummary> =>
  useQuery(sampleQuery(useWorkspaceKeys(), id));

/** Every sample write answers the sample as its page shows it. */
const writeSample = (keys: WorkspaceKeys, sample: SampleSummary): WorkspaceWrite => ({
  set: [[keys.sample(sample.id), sample]],
});

export const useCreateSample = (): UseMutationResult<SampleSummary, Error, SampleInput> =>
  useWorkspaceMutation({
    mutationFn: (input: SampleInput) => researchApi.createSample(input),
    cache: writeSample,
  });

export const useUpdateSample = (
  id: string,
): UseMutationResult<SampleSummary, Error, UpdateSampleInput> =>
  useWorkspaceMutation({
    mutationFn: (input: UpdateSampleInput) => researchApi.updateSample(id, input),
    cache: writeSample,
  });

export const useDeleteSample = (): UseMutationResult<void, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.deleteSample(id),
    cache: (keys, _data, id) => ({ remove: [keys.sample(id)] }),
  });

export const useSendSample = (): UseMutationResult<SampleSummary, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.sendSample(id),
    cache: writeSample,
  });

/** Analysts only. */
export const useReceiveSample = (): UseMutationResult<SampleSummary, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.receiveSample(id),
    cache: writeSample,
  });
